import {useRef, useState} from "react";
import {
    DomainExportQuery,
    useExtensionProfileExportLazyQuery,
    useExtensionDevicesExportLazyQuery,
    useDomainExportLazyQuery,
    useDomainAllocationExportLazyQuery,
    DomainAllocationExportLazyQueryHookResult,
    useExtensionLimitsExportLazyQuery,
    ExtensionLimitsExportLazyQueryHookResult,
    ExtensionDevicesExportLazyQueryHookResult,
    ExtensionProfileExportLazyQueryHookResult,
    PhoneButtonsLazyQueryHookResult,
    usePhoneButtonsLazyQuery,
    SpeedDialsExportLazyQueryHookResult,
    useSpeedDialsExportLazyQuery,
    DomainExportAddressBookQuery,
    useDomainExportAddressBookLazyQuery,
} from "./queries.generated";
import {stringify as yaml_stringify} from "yaml";
import {saveAs} from "file-saver";
import {FormattedPageTitle} from "@/components/pages";
import FormattedButton from "@/components/FormattedButton";
import {AllocationInput, IvrActionType} from "@/api";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faInfoCircle} from "@fortawesome/free-solid-svg-icons";
import {useTheme} from "@mui/material";
import {
    AudioFileWriter,
    DomainExportSection,
    DomainExportYaml,
    ExtensionExport,
    ExtensionQuery,
    FaxExport,
    GatewayExport,
    IvrExport,
    IvrQuery,
    keysToDelete,
    KeysToOmit,
    ProvisionedDeviceExport,
    UpdateIvrButtonExport,
} from "./types";
import {Grid} from "@mui/material";
import {TooltipWrapper} from "@dvrt/graphql-widgets";
import {convertSubmenusExport, ivrActionToExportFormat, ivrMediaToExportFormat} from "@/components/ivr/profiles/tools";
import {useAuth} from "@/context/AuthProvider";
import {DateTime} from "luxon";
import {useDomainNameQuery} from "@/queries.generated";
import {defineMessage} from "react-intl";

const VOICEMAIL_SPEED_DIAL = "vm_listen";

type Props = {
    domainId: number;
};

/** clears the keys that are not needed for the export */
const clearKeys = <T extends object>(data: T): Omit<T, KeysToOmit> => {
    const newData: any = structuredClone(data);
    for (const key of Object.keys(data)) {
        if (keysToDelete.includes(key as KeysToOmit)) {
            delete newData[key];
        } else if (Array.isArray(newData[key])) {
            continue;
        } else if (newData[key] && typeof newData[key] === "object") {
            newData[key] = clearKeys(newData[key]);
        }
    }
    return newData as Omit<T, KeysToOmit>;
};

const convertToYaml = (data: DomainExportYaml): string => yaml_stringify(data, {aliasDuplicateObjects: false});

const allocationMapper = (allocation: {name: string; domainAllocated: number}) => ({
    name: allocation.name,
    value: allocation.domainAllocated,
});

const getDomain = async (
    data: DomainExportQuery,
    allocationQuery: DomainAllocationExportLazyQueryHookResult[0],
): Promise<DomainExportSection> => {
    const domain = data.domains.items[0];

    const {data: allocationData} = await allocationQuery({
        variables: {domainId: domain.id},
        fetchPolicy: "no-cache",
    });

    // even though the allocation input has changed, we still use the old format for compatibility
    const serviceEndpoints = Object.entries(domain).reduce((acc, [key, value]) => {
        if (key.startsWith("quota")) {
            const name = key
                .replace(/quota/, "")
                .replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`)
                .replace(/^_/, "");
            acc.push({name, value});
        }
        return acc;
    }, [] as AllocationInput[]);

    return {
        ...clearKeys(domain),
        allocations: {
            serviceEndpoints,
            userPackages: allocationData.domainAllocation.userPackages.map(allocationMapper),
            deviceTypeSets: allocationData.domainAllocation.deviceTypeSets.map(allocationMapper),
            capabilitySets: allocationData.domainAllocation.capabilitySets.map(allocationMapper),
        },
    };
};

const getExtension = async (
    extension: ExtensionQuery,
    profileQuery: ExtensionProfileExportLazyQueryHookResult[0],
    devicesQuery: ExtensionDevicesExportLazyQueryHookResult[0],
    limitsQuery: ExtensionLimitsExportLazyQueryHookResult[0],
    buttonsQuery: PhoneButtonsLazyQueryHookResult[0],
    speedDialsQuery: SpeedDialsExportLazyQueryHookResult[0],
): Promise<ExtensionExport> => {
    const {data: profileData} = await profileQuery({
        variables: {userId: extension.id},
        fetchPolicy: "no-cache",
    });

    const {data: devicesData} = await devicesQuery({
        variables: {userId: extension.id},
        fetchPolicy: "no-cache",
    });

    const {data: limitsData} = await limitsQuery({
        variables: {userId: extension.id},
        fetchPolicy: "no-cache",
    });

    const {data: speedDialsData} = await speedDialsQuery({
        variables: {userId: extension.id},
        fetchPolicy: "no-cache",
    });

    for (const provisioned of devicesData.provisionedDevices as ProvisionedDeviceExport[]) {
        const buttons = await buttonsQuery({
            variables: {provisionedDeviceId: provisioned.id},
            fetchPolicy: "no-cache",
        });

        provisioned.buttons = buttons.data.phoneButtons ?? [];
    }

    return {
        ...clearKeys(extension),
        profiles: profileData?.userProfiles.sort((a, b) => a.id - b.id).map(clearKeys) ?? [],
        devices: {
            client: devicesData.clientDevices.map(clearKeys),
            thirdParty: devicesData.thirdPartyDevices.map(clearKeys),
            provisioned: (devicesData.provisionedDevices as ProvisionedDeviceExport[]).map(clearKeys),
        },
        limits: {
            capabilitySets: limitsData.userQuotaInfo.capabilitySets ?? [],
            deviceTypeSets:
                limitsData.userQuotaInfo.deviceTypeSets?.map((deviceTypeSet) => ({
                    name: deviceTypeSet.name,
                    value: deviceTypeSet.value,
                })) ?? [],
        },
        speeddials: speedDialsData.speedDials.items.map((speedDial) => ({
            shortNumber: speedDial.shortNumber,
            targetNumber: speedDial.targetNumber,
            description: speedDial.targetNumber === VOICEMAIL_SPEED_DIAL ? VOICEMAIL_SPEED_DIAL : speedDial.description,
        })),
    };
};

const getExtensions = async (
    data: DomainExportQuery,
    profileQuery: ExtensionProfileExportLazyQueryHookResult[0],
    devicesQuery: ExtensionDevicesExportLazyQueryHookResult[0],
    limitsQuery: ExtensionLimitsExportLazyQueryHookResult[0],
    buttonsQuery: PhoneButtonsLazyQueryHookResult[0],
    speedDialsQuery: SpeedDialsExportLazyQueryHookResult[0],
): Promise<ExtensionExport[]> => {
    const extensions = [];
    for (const extension of data.users.items) {
        extensions.push(
            await getExtension(extension, profileQuery, devicesQuery, limitsQuery, buttonsQuery, speedDialsQuery),
        );
    }
    return extensions;
};

const getFaxes = (data: DomainExportQuery): FaxExport[] => {
    return data.faxes.items.map((fax) => {
        const additionalEmails = data.faxesEmails.items
            .filter((email) => email.faxId === fax.id)
            .map((email) => {
                delete email.faxId;
                email.id = 0;
                return structuredClone(email);
            });

        return {
            ...clearKeys(fax),
            additionalEmails,
        };
    });
};

const getGateways = (data: DomainExportQuery): GatewayExport[] => {
    return data.gateways.items.map((gateway) => ({
        ...clearKeys(gateway),
        routings: data.privateRouting.items.filter((pr) => pr.gatewayId === gateway.id),
    }));
};

const getIvrs = async (
    data: DomainExportQuery,
    audioFileWriter: AudioFileWriter,
    accessToken: string,
): Promise<IvrExport[]> => {
    const ivrs = data.ivrs.items;
    const result = [] as IvrExport[];

    type Action = IvrQuery["profiles"][number]["actions"][number];
    type Greeting = IvrQuery["profiles"][number]["greetings"][number];

    const sortButtons = (a: Action, b: Action): number => {
        return a.button - b.button;
    };

    const sortGreetings = (a: Greeting, b: Greeting): number => {
        return a.orderNumber - b.orderNumber;
    };

    const removeSubmenuTargetNumberId = (action: UpdateIvrButtonExport): UpdateIvrButtonExport => {
        if (action.actionType === IvrActionType.Submenu) action.targetNumberId = null; // setting to null, to not break consistency of the file
        return action;
    };

    for (const ivr of ivrs) {
        const convertedIvr = {
            ...clearKeys(ivr),
            profiles: [],
        } as IvrExport;

        ivr.profiles.sort((a, b) => a.id - b.id);
        for (const profile of ivr.profiles) {
            const buttons = (
                await Promise.all(
                    profile.actions
                        .sort(sortButtons)
                        .map((action) => ivrActionToExportFormat(action, audioFileWriter, accessToken)),
                )
            ).map(removeSubmenuTargetNumberId);

            const convertedSubmenus = await convertSubmenusExport(profile, audioFileWriter, accessToken);
            delete profile.actions; // convert submenus still needs the actions, so we delete them afterwards

            const profileGreetings = profile.greetings.filter((greeting) => greeting.isGreeting).sort(sortGreetings);
            const profileMenuMedia = profile.greetings.filter((greeting) => !greeting.isGreeting).sort(sortGreetings);

            const greetings = await Promise.all(
                profileGreetings.map((greeting) =>
                    ivrMediaToExportFormat(greeting, "greeting", audioFileWriter, accessToken),
                ),
            );
            const menuMedia = await Promise.all(
                profileMenuMedia.map((menuMedia) =>
                    ivrMediaToExportFormat(menuMedia, "menuMedia", audioFileWriter, accessToken),
                ),
            );

            convertedIvr.profiles.push({
                ...clearKeys(profile),
                buttons,
                submenus: convertedSubmenus,
                greetings,
                menuMedia,
            });
        }

        result.push(convertedIvr);
    }

    return result;
};

const getAddressBook = (rows: DomainExportAddressBookQuery["addressBook"]["rows"]): DomainExportYaml["addressBook"] =>
    rows.filter((row) => !row.uid).map(clearKeys);

const DomainExport = ({domainId}: Props) => {
    const [downloading, setDownloading] = useState(false);
    const [showInfo, setShowInfo] = useState(false);
    const infoTimeout = useRef<NodeJS.Timeout | null>(null);
    const theme = useTheme();
    const primaryColor = theme.palette.primary.main;
    const auth = useAuth();
    const domainNameQuery = useDomainNameQuery({variables: {id: domainId}});

    const [exportDomain] = useDomainExportLazyQuery({
        variables: {domainId},
        fetchPolicy: "no-cache",
    });
    const [exportDomainAddressBook] = useDomainExportAddressBookLazyQuery({
        variables: {domainName: domainNameQuery.data?.domains.items[0].name},
        fetchPolicy: "no-cache",
    });

    const [domainAllocationQuery] = useDomainAllocationExportLazyQuery({fetchPolicy: "no-cache"});
    const [extensionProfileQuery] = useExtensionProfileExportLazyQuery({fetchPolicy: "no-cache"});
    const [extensionDevicesQuery] = useExtensionDevicesExportLazyQuery({fetchPolicy: "no-cache"});
    const [extensionLimitsQuery] = useExtensionLimitsExportLazyQuery({fetchPolicy: "no-cache"});
    const [phoneButtonsQuery] = usePhoneButtonsLazyQuery({fetchPolicy: "no-cache"});
    const [speedDialsQuery] = useSpeedDialsExportLazyQuery({fetchPolicy: "no-cache"});

    const combineData = async (
        exportDomainData: DomainExportQuery,
        exportDomainAddressBookData: DomainExportAddressBookQuery,
        audioFileWriter: AudioFileWriter,
        accessToken: string,
    ): Promise<DomainExportYaml> => ({
        domain: await getDomain(exportDomainData, domainAllocationQuery),
        users: await getExtensions(
            exportDomainData,
            extensionProfileQuery,
            extensionDevicesQuery,
            extensionLimitsQuery,
            phoneButtonsQuery,
            speedDialsQuery,
        ),
        locations: exportDomainData.locations.items.map(clearKeys),
        ivrs: await getIvrs(exportDomainData, audioFileWriter, accessToken),
        faxes: getFaxes(exportDomainData),
        gateways: getGateways(exportDomainData),
        groups: exportDomainData.groups.items.map((group) => {
            const acwLength = exportDomainData.groupsQueue.items.find((info) => info.groupId === group.id)?.acwLength;
            return clearKeys({...structuredClone(group), acwLength});
        }),
        addressBook: getAddressBook(exportDomainAddressBookData.addressBook.rows),
    });

    const download = async (): Promise<void> => {
        const {default: JSZip} = await import(
            /* webpackChunkName: "jszip" */
            "jszip"
        );

        setDownloading(true);

        infoTimeout.current = setTimeout(() => {
            setShowInfo(true);
        }, 5000);

        const zip = new JSZip();

        const audioFileWriter: AudioFileWriter = (filename, data): void => {
            zip.file(filename, data);
        };

        // TODO: handle errors in the queries
        const [exportDomainData, exportDomainAddressBookData] = await Promise.all([
            exportDomain().then((result) => result.data),
            exportDomainAddressBook().then((result) => result.data),
        ]);

        const yamlJSData = await combineData(
            exportDomainData,
            exportDomainAddressBookData,
            audioFileWriter,
            auth.accessToken,
        );

        const yamlContent = convertToYaml(yamlJSData);
        zip.file("domain.yaml", yamlContent);

        setDownloading((_) => {
            setShowInfo(false);
            return false;
        });

        if (infoTimeout.current) clearTimeout(infoTimeout.current);

        // format the export zip filename
        const domainName = yamlJSData.domain.name;
        const datetime = DateTime.now().toFormat("yyyy-MM-dd-HH-mm-ss");
        const exportFilename = `${domainName}-${datetime}.zip`;

        const blob = await zip.generateAsync({type: "blob"});
        saveAs(blob, exportFilename);
    };

    return (
        <div>
            <FormattedPageTitle desc={defineMessage({id: "domain_export.title", defaultMessage: "Domain export"})} />
            <Grid container spacing={3}>
                <Grid>
                    <FormattedButton
                        message={defineMessage({id: "download", defaultMessage: "Download"})}
                        variant="contained"
                        color="primary"
                        onClick={download}
                        pending={downloading}
                    />
                </Grid>
                <Grid>
                    <div style={{transition: "opacity 1s ease", ...(showInfo ? {opacity: 1} : {opacity: 0})}}>
                        <TooltipWrapper
                            title={defineMessage({
                                id: "domain_export.tooltip",
                                defaultMessage: "This action might take some time.",
                            })}
                            position="bottom"
                            condition={showInfo}
                        >
                            <FontAwesomeIcon icon={faInfoCircle} color={primaryColor} size="2x" />
                        </TooltipWrapper>
                    </div>
                </Grid>
            </Grid>
        </div>
    );
};

export default DomainExport;
